import { fetchResidentSortMetadata, createResidentSortData, fetchCurrentResidents } from '@/api/residents/sortData';
import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { useSelector } from 'react-redux';
import { selfDataSelector } from '@/redux/config/selfData';
import api from '@/api';
import { IMdResident } from '@/types/api/medical/getResidents';
import { IResidentDFreeGraph } from '@/types/residents/dfreeGraph';
import dayjs from 'dayjs';
import React from 'react';
import { useParams } from 'react-router-dom';

// 入居者一覧（DFreeグラフページ）
export const useFetchDFreeGraphList = () => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  const permittedUnitId = selfUser.hasUnitPerm ? selfUser.nursingHomeUnitId : null;

  // 全ての施設向けサービス（serviceType=full)
  const fetchDFreeGrapListData = React.useCallback(
    async (
      start: string,
      end: string
    ) => {
      const residentsReq = fetchCurrentResidents(nursingHomeId, permittedUnitId, false);

      const reactionsReq = api.get('/traversal/nursing-homes/:nursingHomeId/sensor-reactions', {
        params: { nursingHomeId, from: start, to: end, sort: 'asc' },
      });

      const recordsReq = api.get('/traversal/nursing-homes/:nursingHomeId/care-records', {
        params: { nursingHomeId, from: start, to: end, limit: 10000 },
      });

      // activityStatusはstartと同時刻のレコードを取得するために1秒前を指定している
      const activityStatusReq = api
        .get('/traversal/nursing-homes/:nursingHomeId/activity-status-analysis', {
          params: { nursingHomeId, from: formatUtcFetchTimestamp(dayjs(start).subtract(1, 'second')), to: end },
        })
        .catch((err) => {
          if (err?.response?.status === 404) {
            return { nursingHomeId: +nursingHomeId, residents: [] };
          }
          throw err;
        });

      const [residentList, reactions, records, activityStatuses, metadata] = await Promise.all([
        residentsReq,
        reactionsReq,
        recordsReq,
        activityStatusReq,
        fetchResidentSortMetadata(nursingHomeId, permittedUnitId),
      ]);

      const sortData = createResidentSortData(residentList, metadata);
      const metadataById = new Map(metadata.map((resident) => [resident.id, resident]));
      const allResidents = (residentList as IResidentDFreeGraph[]).map((resident) => ({
        ...resident,
        sortData: sortData.get(resident.id)!,
        filterData: {
          nursingHomeUnitId: metadataById.get(resident.id)!.nursingHomeUnitId,
          deviceAssigned: resident.serialNumber != null || resident.gasSerialNumber != null,
          deviceAttached: resident.urineSize != null || resident.gasPercentage != null,
        },
      }));

      let residents = allResidents.map((resident) => {
        const sensor = reactions.residents.find((v) => v.residentId === resident.id);
        const activityStatus = activityStatuses?.residents?.find((v) => v.residentId === resident.id);
        if (!sensor) {
          return {
            ...resident,
            rawSensorReactions: [],
            activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
          };
        }
        const destSensorReactions: [number, number | null][] = [];
        const destRawSensorReactions: [number, number | null][] = [];
        let prevTimestamp = 0;

        sensor.sensorReactions.forEach((item) => {
          // 前後から25分以上離れていたら切断
          const timestamp = Date.parse(item.timestamp);
          if (timestamp - prevTimestamp > 60 * 1000 * 25) {
            destSensorReactions.push([timestamp - 60 * 1000, null]);
            destRawSensorReactions.push([timestamp - 60 * 1000, null]);
          }
          prevTimestamp = timestamp;

          destSensorReactions.push([timestamp, item.u1xActiveRatio * 10]);
          destRawSensorReactions.push([timestamp, item.activeRatio * 10]);
        });

        // care recored plots. want refactor.
        const careResidents = records?.residents;
        const careRecords = careResidents.find((v) => v.residentId === resident.id);
        if (!careRecords || Object.keys(careRecords.allCareRecords).length == 0) {
          return {
            ...resident,
            sensorReactions: destSensorReactions,
            rawSensorReactions: destRawSensorReactions,
            sensorNotifications: sensor.notifications,
            careRecordPlots: [],
            activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
          };
        }
        const careRecordPlots = careRecords.allCareRecords.map((row) => {
          const v = row;
          return {
            timestamp: v.timestamp,
            toilet: v.urinatedToilet,
            pad: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
            ruv: v.ruv,
            accumulation: v.accumulation,
          };
        });

        return {
          ...resident,
          sensorReactions: destSensorReactions,
          rawSensorReactions: destRawSensorReactions,
          sensorNotifications: sensor.notifications,
          careRecordPlots: careRecordPlots,
          activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
        };
      });

      return residents;
    },
    [nursingHomeId, permittedUnitId]
  );

  // 医療向けサービスのみ（serviceType=medical）
  const fetchMdDFreeGrapListData = React.useCallback(
    async (
      start: string,
      end: string
    ) => {
      const residentsReq = fetchCurrentResidents(nursingHomeId, permittedUnitId, true);

      const reactionsReq = api.get('/traversal/md/nursing-homes/:nursingHomeId/visualized-urine-volume', {
        params: { nursingHomeId, from: start, to: end, sort: 'asc' },
      });

      const recordsReq = api.get('/traversal/nursing-homes/:nursingHomeId/care-records', {
        params: { nursingHomeId, from: start, to: end, limit: 10000 },
      });

      const [residentList, reactions, records, metadata] = await Promise.all([residentsReq, reactionsReq, recordsReq, fetchResidentSortMetadata(nursingHomeId, permittedUnitId)]);

      const sortData = createResidentSortData(residentList, metadata);
      const metadataById = new Map(metadata.map((resident) => [resident.id, resident]));
      const allResidents = (residentList as IMdResident[]).map((resident) => ({
        ...resident,
        sortData: sortData.get(resident.id)!,
        filterData: {
          nursingHomeUnitId: metadataById.get(resident.id)!.nursingHomeUnitId,
          deviceAssigned: resident.serialNumber != null,
          deviceAttached: resident.isDeviceActive,
        },
      }));

      let residents = allResidents.map((resident) => {
        const sensor = reactions.residents.find((v) => v.residentId === resident.id);
        if (!sensor) return resident;
        const destSensorReactions: { values: [number, number | null][]; volumes: [number, number | null][] } = {
          values: [],
          volumes: [],
        };
        let prevTimestamp: number;

        sensor.visualizedUrineVolumes.forEach((item) => {
          // 前後から25分以上離れていたら切断
          const timestamp = Date.parse(item.timestamp);
          if (timestamp - prevTimestamp > 60 * 1000 * 25) {
            destSensorReactions.values.push([timestamp - 60 * 1000, null]);
            destSensorReactions.volumes.push([timestamp - 60 * 1000, null]);
          }
          prevTimestamp = timestamp;

          // 補正後のvalueを10で割り、整数値に四捨五入する
          destSensorReactions.values.push([
            timestamp,
            Math.round((item['value'] || 0) / 10),
          ]);
          destSensorReactions.volumes.push([timestamp, item['volume']]);
        });

        // care recored plots. want refactor.
        const careResidents = records?.residents;
        const careRecords = careResidents.find((v) => v.residentId === resident.id);
        if (!careRecords || Object.keys(careRecords.allCareRecords).length == 0) {
          return {
            ...resident,
            sensorReactions: destSensorReactions,
            sensorNotifications: sensor.notifications,
            careRecordPlots: [],
          };
        }
        const careRecordPlots = careRecords.allCareRecords.map((row) => {
          const v = row;
          return {
            timestamp: v.timestamp,
            toilet: v.urinatedToilet,
            pad: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
          };
        });

        return {
          ...resident,
          sensorReactions: destSensorReactions,
          sensorNotifications: sensor.notifications,
          careRecordPlots: careRecordPlots,
        };
      });

      return residents;
    },
    [nursingHomeId, permittedUnitId]
  );

  return { fetchDFreeGrapListData, fetchMdDFreeGrapListData };
};
